/* Supply Access motion + small UI helpers
   - [data-bb-reveal] / [data-bb-stagger]: add .bb-on when scrolled into view
   - [data-bb-count]: numbers roll up to their value when visible (stats band)
   - [data-bb-carousel]: dots for the mobile swipe rows
*/
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-bb-reveal]:not(.bb-on), [data-bb-stagger]:not(.bb-on)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('bb-on'); });
      return;
    }
    function show(el) {
      el.classList.add('bb-on');
      /* cards inside a swipe row reveal together: the off-screen ones never intersect until swiped */
      var track = el.parentElement && el.parentElement.hasAttribute('data-bb-carousel') ? el.parentElement : null;
      if (track) {
        Array.prototype.forEach.call(track.children, function (c) {
          if (c.hasAttribute('data-bb-reveal') || c.hasAttribute('data-bb-stagger')) c.classList.add('bb-on');
        });
      }
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            show(e.target);
            io.unobserve(e.target);
          }
        });
      },
      /* fire as soon as any part is inside the viewport (tall elements never reach a 12% ratio on
         a phone), slightly before it scrolls in so the motion is done when the eye lands on it */
      { threshold: 0, rootMargin: '0px 0px 8% 0px' }
    );
    els.forEach(function (el) { io.observe(el); });

    /* Safety net for the cases IntersectionObserver misses on iOS (elements inside clipped
       containers, layout shifts after load, bfcache restores): on every scroll, reveal anything
       whose box is already inside the viewport. Cheap, throttled to one frame. */
    var pending = Array.prototype.slice.call(els);
    var ticking = false;
    function sweep() {
      ticking = false;
      if (!pending.length) return;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      pending = pending.filter(function (el) {
        if (el.classList.contains('bb-on')) return false;
        var r = el.getBoundingClientRect();
        if (r.top < vh * 1.05 && r.bottom > 0 && (r.width || r.height)) { show(el); return false; }
        return true;
      });
      if (!pending.length) document.removeEventListener('scroll', onScroll, true);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(sweep); } }
    /* capture phase: on desktop Horizon scrolls inside .page-wrapper, not the window, and
       scroll events do not bubble, so a window listener would never fire there */
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('pageshow', onScroll);
    setTimeout(sweep, 300);
    setTimeout(sweep, 1500);
  }

  /* ---------- count-up ---------- */
  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
  function formatLike(sample, n, decimals) {
    var s = n.toFixed(decimals);
    if (/,/.test(sample) || (decimals === 0 && n >= 1000)) {
      s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return s;
  }
  function runCount(el) {
    var raw = el.getAttribute('data-bb-count') || el.textContent;
    var m = raw.match(/-?[\d,]*\.?\d+/);
    if (!m) { el.classList.add('bb-counted'); return; }
    var numStr = m[0];
    var target = parseFloat(numStr.replace(/,/g, ''));
    if (isNaN(target)) { el.classList.add('bb-counted'); return; }
    var decimals = (numStr.split('.')[1] || '').length;
    var prefix = raw.slice(0, m.index);
    var suffix = raw.slice(m.index + numStr.length);
    var dur = parseInt(el.getAttribute('data-bb-count-ms') || '1100', 10);
    var delay = parseInt(el.getAttribute('data-bb-count-delay') || '0', 10);
    if (reduce) { el.textContent = raw; el.classList.add('bb-counted'); return; }
    el.textContent = prefix + formatLike(numStr, 0, decimals) + suffix;
    setTimeout(function () {
      var start = null;
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        el.textContent = raw;
        el.classList.add('bb-counted');
      }
      function frame(ts) {
        if (done) return;
        if (!start) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        var v = target * easeOut(p);
        el.textContent = prefix + formatLike(numStr, v, decimals) + suffix;
        if (p < 1) requestAnimationFrame(frame);
        else finish();
      }
      requestAnimationFrame(frame);
      /* safety: background tabs pause rAF, so never leave a number half-counted */
      setTimeout(finish, dur + 400);
    }, delay);
  }
  function initCount() {
    var els = document.querySelectorAll('[data-bb-count]:not(.bb-counted):not(.bb-counting)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) { els.forEach(runCount); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('bb-counting');
          runCount(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- carousel dots ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-bb-carousel]').forEach(function (track) {
      if (track.dataset.bbDotsReady) return;
      track.dataset.bbDotsReady = '1';
      var items = Array.prototype.slice.call(track.children);
      if (items.length < 2) return;
      var dots = document.createElement('div');
      dots.className = 'bb-dots';
      dots.setAttribute('aria-hidden', 'true');
      items.forEach(function (item, i) {
        var b = document.createElement('button');
        b.type = 'button';
        if (i === 0) b.classList.add('bb-on');
        b.addEventListener('click', function () {
          track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(b);
      });
      track.insertAdjacentElement('afterend', dots);
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var x = track.scrollLeft + track.clientWidth * 0.3;
          var idx = 0;
          items.forEach(function (item, i) { if (item.offsetLeft - track.offsetLeft <= x) idx = i; });
          dots.querySelectorAll('button').forEach(function (b, i) { b.classList.toggle('bb-on', i === idx); });
        });
      }, { passive: true });
    });
  }

  function init() { initReveal(); initCount(); initCarousels(); }
  /* re-run for content shown later (e.g. "Show all photos" on the product page) */
  window.bbReveal = initReveal;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* re-run when the theme editor re-renders a section */
  document.addEventListener('shopify:section:load', init);
})();
